import { useCallback, useMemo, useState } from "react";
import { Form, useToast, type FormInstance } from "@ir/ui";
import {
  CreateMasterDataRetailerMatchRuleDTO,
  UpdateMasterDataRetailerMatchRuleDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataRetailerMatchRule } from "@src/domain/entities/masterData";
import {
  CreateMasterDataRetailerMatchRuleUseCase,
  UpdateMasterDataRetailerMatchRuleUseCase,
} from "@src/application/useCases/masterData";
import { MasterDataRetailerMatchRuleRepository } from "@src/infrastructure/repositories/masterData";
import {
  type RetailerMatchRuleFormMode,
  type RetailerMatchRuleFormValues,
} from "./MasterDataRetailerMatchRuleAddEditForm";
import { getMasterDataSaveErrorMessage } from "./masterDataSaveErrors";
import {
  DEFAULT_RETAILER_MATCH_PRIORITY,
  isInvalidSourceColumnError,
  isMatchPatternDuplicateError,
  MATCH_PATTERN_ALREADY_EXISTS_MESSAGE,
  resolveDefaultRetailerMatchSourceColumn,
  SOURCE_COLUMN_INVALID_MESSAGE,
} from "./retailerChildValidation";

interface UseMasterDataRetailerMatchRuleFormOptions {
  onSaved: () => void;
  lockedRetailerId: number | null;
  lockedRetailerName: string;
  sourceColumns: readonly string[];
}

export function useMasterDataRetailerMatchRuleForm({
  onSaved,
  lockedRetailerId,
  lockedRetailerName,
  sourceColumns,
}: UseMasterDataRetailerMatchRuleFormOptions) {
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<RetailerMatchRuleFormValues>();
  const [formMode, setFormMode] = useState<RetailerMatchRuleFormMode | null>(
    null,
  );
  const [editingEntity, setEditingEntity] =
    useState<MasterDataRetailerMatchRule | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);

  const repository = useMemo(
    () => new MasterDataRetailerMatchRuleRepository(),
    [],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataRetailerMatchRuleUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataRetailerMatchRuleUseCase(repository),
    [repository],
  );

  const initialValues = useMemo((): RetailerMatchRuleFormValues => {
    if (formMode === "edit" && editingEntity) {
      return {
        matchPattern: editingEntity.matchPattern,
        matchPriority: String(editingEntity.matchPriority),
        sourceColumn: editingEntity.sourceColumn,
      };
    }
    return {
      matchPattern: "",
      matchPriority: String(DEFAULT_RETAILER_MATCH_PRIORITY),
      sourceColumn: resolveDefaultRetailerMatchSourceColumn(sourceColumns),
    };
  }, [editingEntity, formMode, sourceColumns]);

  const closeForm = useCallback(
    (formInstance?: FormInstance<RetailerMatchRuleFormValues>) => {
      setFormMode(null);
      setEditingEntity(null);
      setIsFormSubmittable(false);
      (formInstance ?? form).resetFields();
    },
    [form],
  );

  const openCreate = useCallback(() => {
    setEditingEntity(null);
    setFormMode("create");
    setIsFormSubmittable(false);
  }, []);

  const openEdit = useCallback((entity: MasterDataRetailerMatchRule) => {
    setEditingEntity(entity);
    setFormMode("edit");
    setIsFormSubmittable(false);
  }, []);

  const handleSubmit = useCallback(
    async (values: RetailerMatchRuleFormValues) => {
      setIsSaving(true);
      try {
        const matchPattern = values.matchPattern.trim();
        const sourceColumn = values.sourceColumn.trim();
        const matchPriority = Number.parseInt(values.matchPriority, 10);
        const priority = Number.isFinite(matchPriority)
          ? matchPriority
          : DEFAULT_RETAILER_MATCH_PRIORITY;

        if (formMode === "edit" && editingEntity) {
          await updateUseCase.execute(
            new UpdateMasterDataRetailerMatchRuleDTO({
              ruleId: editingEntity.ruleId,
              matchPattern,
              matchPriority: priority,
              sourceColumn,
            }),
          );
        } else {
          if (lockedRetailerId == null || lockedRetailerId <= 0) {
            errorToast({ content: "Retailer is required" });
            return;
          }
          await createUseCase.execute(
            new CreateMasterDataRetailerMatchRuleDTO({
              retailerId: lockedRetailerId,
              matchPattern,
              matchPriority: priority,
              sourceColumn,
            }),
          );
        }

        successToast({ content: "Retailer match rule saved successfully" });
        closeForm();
        onSaved();
      } catch (err) {
        if (isInvalidSourceColumnError(err)) {
          form.setFields([
            {
              name: "sourceColumn",
              errors: [SOURCE_COLUMN_INVALID_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else if (isMatchPatternDuplicateError(err)) {
          form.setFields([
            {
              name: "matchPattern",
              errors: [MATCH_PATTERN_ALREADY_EXISTS_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: getMasterDataSaveErrorMessage(
              err,
              "Failed to save match rule. Please try again.",
              "Failed to save match rule. Please try again later.",
            ),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeForm,
      createUseCase,
      editingEntity,
      errorToast,
      form,
      formMode,
      lockedRetailerId,
      onSaved,
      successToast,
      updateUseCase,
    ],
  );

  return {
    form,
    formMode,
    isSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    initialValues,
    lockedRetailerName,
    openCreate,
    openEdit,
    closeForm,
    handleSubmit,
  };
}
